import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { startGoogleSignIn } from "@/lib/googleAuth";
import { AdminShell } from "@/pages/AdminHome";
import { artworkUrl } from "@/pages/PodcastEpisode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK = {
  number: "",
  parsha: "",
  parsha_he: "",
  date: "",
  publish_at: "",
  blurb: "",
  video_id: "",
  artwork_url: "",
  published: true,
};

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

// A future publish_at keeps the episode off the public site until that day.
function scheduleLabel(ep) {
  if (!ep.published) return "draft";
  if (ep.publish_at && ep.publish_at > todayIso()) return `scheduled for ${ep.publish_at}`;
  return "";
}

const field = { backgroundColor: "#241246", border: "1px solid #1A0F3C", color: "#F5EFE3" };
const goldBtn =
  "inline-flex items-center justify-center whitespace-nowrap rounded-full px-6 h-11 text-xs uppercase transition-colors hover:bg-[#F2D589]/[0.12] disabled:opacity-50";
const goldStyle = {
  backgroundColor: "transparent",
  color: "#F2D589",
  border: "1px solid #F2D589",
  letterSpacing: "0.18em",
  fontWeight: 500,
};
const plainBtn =
  "inline-flex items-center justify-center whitespace-nowrap rounded-full px-5 h-11 text-xs uppercase transition-colors hover:border-[#FCA07A] hover:text-[#FFD9C2]";
const plainStyle = { border: "1px solid #241246", color: "#C6BBA9", letterSpacing: "0.18em" };

function Label({ children }) {
  return (
    <span className="block text-[11px] uppercase mb-2" style={{ color: "#C6BBA9", letterSpacing: "0.2em" }}>
      {children}
    </span>
  );
}

export default function PodcastAdmin() {
  const { user, loading, loginEmail } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const [episodes, setEpisodes] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [denied, setDenied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fetchingParsha, setFetchingParsha] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/podcast/episodes`, { withCredentials: true });
      setEpisodes(data.episodes || []);
      setDenied(false);
    } catch (err) {
      if (err?.response?.status === 403) setDenied(true);
      else if (err?.response?.status !== 401) toast.error(formatApiErrorDetail(err));
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await loginEmail(email.trim(), password);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.number || !form.parsha.trim()) {
      toast.error("An episode number and a Parsha, minimum.");
      return;
    }
    setSaving(true);
    const body = { ...form, number: Number(form.number) };
    try {
      if (editingId) {
        await axios.put(`${API}/admin/podcast/episodes/${editingId}`, body, { withCredentials: true });
        toast.success("Episode updated.");
      } else {
        await axios.post(`${API}/admin/podcast/episodes`, body, { withCredentials: true });
        toast.success("Episode added.");
      }
      setForm(BLANK);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setSaving(false);
    }
  };

  const edit = (ep) => {
    setEditingId(ep.id);
    setForm({
      number: String(ep.number ?? ""),
      parsha: ep.parsha || "",
      parsha_he: ep.parsha_he || "",
      date: ep.date || "",
      publish_at: ep.publish_at || "",
      blurb: ep.blurb || "",
      video_id: ep.video_id || "",
      artwork_url: ep.artwork_url || "",
      published: ep.published,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const uploadArtwork = async (file) => {
    if (!file) return;
    setUploading(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/podcast/artwork`, body, { withCredentials: true });
      setForm((f) => ({ ...f, artwork_url: data.artwork_url }));
      toast.success("Artwork uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Sefaria knows what's read on the Shabbos of a given week — publish date in,
  // parsha out. No date passed means the coming Shabbos.
  const fetchParsha = async (isoDate) => {
    setFetchingParsha(true);
    try {
      const { data } = await axios.get(`${API}/parsha`, { params: isoDate ? { date: isoDate } : {} });
      setForm((f) => ({ ...f, parsha: data.en || f.parsha, parsha_he: data.he || f.parsha_he }));
      toast.success(`${data.en} — Shabbos ${data.shabbos}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setFetchingParsha(false);
    }
  };

  const remove = async (ep) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Delete episode ${ep.number} — ${ep.parsha}?`)) return;
    try {
      await axios.delete(`${API}/admin/podcast/episodes/${ep.id}`, { withCredentials: true });
      toast.success("Deleted.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    }
  };

  const shell = (children) => <AdminShell active="podcast">{children}</AdminShell>;

  if (loading) return shell(<p className="mt-20 text-sm" style={{ color: "#C6BBA9" }}>One moment…</p>);

  if (!user) {
    return shell(
      <form onSubmit={doLogin} className="mt-16 max-w-[24rem]" data-testid="admin-login-form">
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}>
          Episode admin
        </h1>
        <p className="mt-4 text-sm" style={{ color: "#C6BBA9" }}>
          Sign in with an admin address.
        </p>
        <button
          type="button"
          onClick={() => startGoogleSignIn("/admin/podcast")}
          className={`${goldBtn} mt-8 w-full`}
          style={goldStyle}
          data-testid="admin-google-signin"
        >
          Continue with Google
        </button>
        <div className="mt-8 flex items-center gap-4">
          <span className="h-px flex-1" style={{ backgroundColor: "#241246" }} />
          <span className="text-[11px] uppercase" style={{ color: "#6B7A82", letterSpacing: "0.2em" }}>
            or
          </span>
          <span className="h-px flex-1" style={{ backgroundColor: "#241246" }} />
        </div>
        <div className="mt-8 flex flex-col gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            autoComplete="email"
            data-testid="admin-email"
            className="h-12 w-full rounded-md px-4 text-sm outline-none"
            style={field}
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            autoComplete="current-password"
            data-testid="admin-password"
            className="h-12 w-full rounded-md px-4 text-sm outline-none"
            style={field}
          />
          <button type="submit" disabled={busy} className={`${goldBtn} mt-2`} style={goldStyle} data-testid="admin-login-submit">
            {busy ? "Signing in" : "Sign in"}
          </button>
        </div>
      </form>
    );
  }

  if (denied || !user.is_admin) {
    return shell(
      <div className="mt-20" data-testid="admin-denied">
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}>
          Not your panel.
        </h1>
        <p className="mt-4 text-sm" style={{ color: "#C6BBA9" }}>
          {user.email} isn&apos;t on the admin list.
        </p>
      </div>
    );
  }

  return shell(
    <>
      <h1 className="mt-14" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}>
        {editingId ? "Edit episode" : "Add an episode"}
      </h1>

      <form onSubmit={save} className="mt-10 grid gap-5 sm:grid-cols-2" data-testid="episode-form">
        <label>
          <Label>Episode number</Label>
          <input
            type="number"
            min="0"
            value={form.number}
            onChange={(e) => setForm({ ...form, number: e.target.value })}
            data-testid="episode-number"
            className="h-12 w-full rounded-md px-4 text-sm outline-none"
            style={field}
          />
        </label>
        <label>
          <Label>Goes live on</Label>
          <input
            type="date"
            value={form.publish_at}
            onChange={(e) => setForm({ ...form, publish_at: e.target.value })}
            data-testid="episode-publish-at"
            className="h-12 w-full rounded-md px-4 text-sm outline-none"
            style={field}
          />
          <span className="mt-2 block text-[11px]" style={{ color: "#6B7A82" }}>
            Leave blank to publish right away. A future date hides it until then.
          </span>
        </label>
        <label className="sm:col-span-2">
          <Label>Date shown on the site (optional)</Label>
          <input
            type="text"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            placeholder="e.g. 18 Elul 5786 · 11 September 2026"
            data-testid="episode-date"
            className="h-12 w-full rounded-md px-4 text-sm outline-none"
            style={field}
          />
          <span className="mt-2 block text-[11px]" style={{ color: "#6B7A82" }}>
            Free text, so you can include the Hebrew date. Blank falls back to the live date.
          </span>
        </label>
        <label>
          <Label>Parsha (English)</Label>
          <input
            type="text"
            value={form.parsha}
            onChange={(e) => setForm({ ...form, parsha: e.target.value })}
            placeholder="Ki Teitzei"
            data-testid="episode-parsha"
            className="h-12 w-full rounded-md px-4 text-sm outline-none"
            style={field}
          />
        </label>
        <label>
          <Label>Parsha (Hebrew)</Label>
          <input
            type="text"
            dir="rtl"
            value={form.parsha_he}
            onChange={(e) => setForm({ ...form, parsha_he: e.target.value })}
            placeholder="כי תצא"
            data-testid="episode-parsha-he"
            className="h-12 w-full rounded-md px-4 text-sm outline-none"
            style={{ ...field, fontFamily: "'Frank Ruhl Libre', serif" }}
          />
        </label>
        <label className="sm:col-span-2">
          <Label>YouTube video ID</Label>
          <input
            type="text"
            value={form.video_id}
            onChange={(e) => setForm({ ...form, video_id: e.target.value })}
            placeholder="the part after watch?v="
            data-testid="episode-video-id"
            className="h-12 w-full rounded-md px-4 text-sm outline-none"
            style={field}
          />
        </label>
        <div className="sm:col-span-2">
          <Label>Share artwork</Label>
          <div className="flex flex-wrap items-start gap-6">
            <div
              className="shrink-0"
              style={{ width: 220, aspectRatio: "16 / 9", backgroundColor: "#0C0826", border: "1px solid #241246" }}
            >
              {artworkUrl(form) ? (
                <img
                  src={artworkUrl(form)}
                  alt="Episode artwork"
                  className="h-full w-full object-cover"
                  data-testid="episode-artwork-preview"
                />
              ) : (
                <span className="flex h-full w-full items-center justify-center text-[11px]" style={{ color: "#6B7A82" }}>
                  No artwork
                </span>
              )}
            </div>
            <div className="flex flex-col gap-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => uploadArtwork(e.target.files?.[0])}
                data-testid="episode-artwork-file"
                className="text-xs"
                style={{ color: "#C6BBA9" }}
              />
              <span className="text-[11px]" style={{ color: "#6B7A82", maxWidth: "34ch", lineHeight: 1.6 }}>
                {uploading
                  ? "Uploading…"
                  : form.artwork_url
                  ? "Uploaded. This is what shows when the link is shared."
                  : "Leave it blank and we'll use YouTube's own thumbnail."}
              </span>
              {form.artwork_url && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, artwork_url: "" })}
                  className={plainBtn}
                  style={plainStyle}
                  data-testid="episode-artwork-clear"
                >
                  Use YouTube thumbnail
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="sm:col-span-2 flex flex-wrap gap-3" data-testid="episode-parsha-fill">
          <button
            type="button"
            disabled={fetchingParsha || !form.publish_at}
            onClick={() => fetchParsha(form.publish_at)}
            className={plainBtn}
            style={plainStyle}
            data-testid="episode-parsha-from-date"
            title={form.publish_at ? "" : "Set a live date first"}
          >
            {fetchingParsha ? "Asking Sefaria" : "Fill parsha from live date"}
          </button>
          <button
            type="button"
            disabled={fetchingParsha}
            onClick={() => fetchParsha("")}
            className={plainBtn}
            style={plainStyle}
            data-testid="episode-parsha-this-week"
          >
            This week&apos;s parsha
          </button>
        </div>

        <label className="sm:col-span-2">
          <Label>Blurb</Label>
          <textarea
            rows={4}
            value={form.blurb}
            onChange={(e) => setForm({ ...form, blurb: e.target.value })}
            data-testid="episode-blurb"
            className="w-full rounded-md px-4 py-3 text-sm outline-none"
            style={field}
          />
        </label>
        <label className="sm:col-span-2 flex items-center gap-3 text-sm" style={{ color: "#C6BBA9" }}>
          <input
            type="checkbox"
            checked={form.published}
            onChange={(e) => setForm({ ...form, published: e.target.checked })}
            data-testid="episode-published"
          />
          Published (uncheck to keep it as a draft)
        </label>

        <div className="sm:col-span-2 flex flex-wrap gap-3">
          <button type="submit" disabled={saving} className={goldBtn} style={goldStyle} data-testid="episode-save">
            {saving ? "Saving" : editingId ? "Save changes" : "Add episode"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setForm(BLANK);
              }}
              className={plainBtn}
              style={plainStyle}
              data-testid="episode-cancel"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <h2 className="mt-20" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-1)" }}>
        {episodes.length} episode{episodes.length === 1 ? "" : "s"}
      </h2>
      <ul className="mt-8 flex flex-col" data-testid="episode-list">
        {episodes.map((ep, i) => (
          <li
            key={ep.id}
            className="flex flex-wrap items-center justify-between gap-4 py-6"
            style={{ borderTop: i === 0 ? "none" : "1px solid #241246" }}
            data-testid={`episode-row-${ep.number}`}
          >
            <div>
              <p className="text-[11px] uppercase" style={{ color: "#C6BBA9", letterSpacing: "0.2em" }}>
                {ep.number} · {ep.date || ep.publish_at || "no date"}
                {scheduleLabel(ep) ? ` · ${scheduleLabel(ep)}` : ""}
              </p>
              <p className="mt-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.35rem", fontWeight: 300 }}>
                {ep.parsha}{" "}
                <span dir="rtl" style={{ fontFamily: "'Frank Ruhl Libre', serif", color: "#F2D589" }}>
                  {ep.parsha_he}
                </span>
              </p>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => edit(ep)} className={plainBtn} style={plainStyle} data-testid={`episode-edit-${ep.number}`}>
                Edit
              </button>
              <button
                type="button"
                onClick={() => remove(ep)}
                className={plainBtn}
                style={{ ...plainStyle, color: "#F2D589" }}
                data-testid={`episode-delete-${ep.number}`}
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
